import { Injectable, signal } from '@angular/core';

export interface AssistContext {
  universityId?: string | null;
  universityName?: string | null;
  intake?: string | null;
  intakeName?: string | null;
  page?: string | null;
}

/**
 * What the assistant is currently "looking at".
 *
 * The screens publish into this; the assistant reads it and sends it with every
 * question. That is why "what is open here?" works without naming a university
 * - and why the answer cannot drift to a different one, because the backend
 * applies this as a hard filter rather than as a hint in the prompt.
 *
 * Scope narrows as the viewer clicks in:
 *   university list   -> nothing set, questions are global
 *   one university    -> universityId set
 *   intake pop-up     -> universityId + intake set
 *
 * It is deliberately small: ids and names only, never the data itself. The
 * figures are fetched server-side from the same endpoints the widgets use, so
 * there is one source of truth and nothing to keep in sync here.
 */
@Injectable({ providedIn: 'root' })
export class AssistContextService {
  readonly context = signal<AssistContext>({});

  /**
   * Whether the assistant panel is open.
   *
   * Here rather than inside the component because the panel is opened from
   * elsewhere too - Junaid asked for an "AI Analysis" button beside "Add
   * University" as well as the floating one.
   */
  readonly panelOpen = signal(false);

  openPanel(): void {
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  togglePanel(): void {
    this.panelOpen.update((v) => !v);
  }

  /** Entering one university. Clears any intake from a previous one. */
  setUniversity(universityId: string | null, universityName?: string | null): void {
    const current = this.context();
    const changed = String(current.universityId || '') !== String(universityId || '');
    this.context.set({
      ...current,
      universityId: universityId || null,
      universityName: universityName || null,
      ...(changed ? { intake: null, intakeName: null } : {}),
    });
  }

  /** An intake pop-up opened - the narrowest scope the assistant has. */
  setIntake(
    universityId: string | null,
    universityName: string | null,
    intake: string | null,
    intakeName?: string | null
  ): void {
    this.context.set({
      ...this.context(),
      universityId: universityId || null,
      universityName: universityName || null,
      intake: intake || null,
      intakeName: intakeName || null,
    });
  }

  /**
   * The pop-up closed. The university is kept: the viewer is still on its page,
   * and dropping it would make the next question answer about everything.
   */
  clearIntake(): void {
    this.context.set({ ...this.context(), intake: null, intakeName: null });
  }

  setPage(page: string | null): void {
    this.context.set({ ...this.context(), page: page || null });
  }

  /** Back to global scope, from the assistant's own context chip. */
  clear(): void {
    this.context.set({ page: this.context().page ?? null });
  }
}
